import urlFor, { QueryParams } from 'shared/tools/url-helper'

import { BookProps } from 'client/shared/types/book'
import { ShelfProps } from 'client/shared/types/shelf'
import { AudiobookProps } from 'client/shared/types/audiobook'
import { ComicbookProps } from 'client/shared/types/comicbook'
import { UserLong } from 'client/shared/types/user'
import { SeriesProps } from 'client/shared/types/series'
import { SerialProps } from 'client/shared/types/serial'

export type NavItem = {
  path: string
  message: string
  count: number
  bypassAuth?: boolean
  hasUpdatedSeries?: boolean
  pseudoLink?: boolean
}

export default {
  bookNav(book: BookProps, query?: QueryParams): NavItem[] {
    const {
      uuid,
      readers_count = 0,
      bookshelves_count = 0,
      quotes_count = 0,
      impressions_count = 0,
    } = book

    return [
      {
        path: urlFor.bookReaders(uuid, query),
        message: 'book.readers',
        count: readers_count,
        pseudoLink: true,
      },
      {
        path: urlFor.bookShelves(uuid, query),
        message: 'book.shelves',
        count: bookshelves_count,
        bypassAuth: true,
      },
      {
        path: urlFor.bookImpressions(uuid, query),
        message: 'book.impressions',
        count: impressions_count,
        bypassAuth: true,
      },
      {
        path: urlFor.bookQuotes(uuid, query),
        message: 'book.quotes',
        count: quotes_count,
        bypassAuth: true,
      },
    ]
  },

  serialNav(serial: SerialProps, query?: QueryParams): NavItem[] {
    const {
      uuid,
      readers_count = 0,
      quotes_count = 0,
      impressions_count = 0,
      bookshelves_count = 0,
      episodes_count = 0,
    } = serial

    return [
      {
        path: urlFor.serialEpisodes(uuid, query),
        message: 'book.episodes',
        count: episodes_count,
        bypassAuth: true,
      },
      {
        path: urlFor.serialReaders(uuid, query),
        message: 'book.readers',
        count: readers_count,
      },
      {
        path: urlFor.serialShelves(uuid, query),
        message: 'book.shelves',
        count: bookshelves_count,
        bypassAuth: true,
      },
      {
        path: urlFor.serialQuotes(uuid, query),
        message: 'book.quotes',
        count: quotes_count,
        bypassAuth: true,
      },
      {
        path: urlFor.serialImpressions(uuid, query),
        message: 'book.impressions',
        count: impressions_count,
        bypassAuth: true,
      },
    ]
  },

  shelfNav(shelf: ShelfProps, query?: QueryParams): NavItem[] {
    const { uuid, followers_count, posts_count } = shelf

    return [
      {
        path: urlFor.shelfBooks(uuid, query),
        message: 'shelf.books',
        bypassAuth: true,
        count: posts_count,
      },
      {
        path: urlFor.shelfFollowers(uuid, query),
        message: 'shelf.followers',
        count: followers_count,
        pseudoLink: true,
      },
    ]
  },

  audioBookNav(audiobook: AudiobookProps, query?: QueryParams): NavItem[] {
    const { uuid, listeners_count = 0, impressions_count = 0 } = audiobook

    return [
      {
        path: urlFor.audioBookListeners(uuid, query),
        message: 'book.listeners',
        count: listeners_count,
        pseudoLink: true,
      },
      {
        path: urlFor.audioBookImpressions(uuid, query),
        message: 'book.impressions',
        count: impressions_count,
        bypassAuth: true,
      },
    ]
  },

  comicbookNav(comicbook: ComicbookProps, query?: QueryParams): NavItem[] {
    const {
      uuid,
      readers_count,
      bookshelves_count,
      impressions_count,
    } = comicbook

    return [
      {
        path: urlFor.comicbookReaders(uuid, query),
        message: 'book.readers',
        count: readers_count,
        pseudoLink: true,
      },
      {
        path: urlFor.comicbookShelves(uuid, query),
        message: 'book.shelves',
        count: bookshelves_count,
        bypassAuth: true,
      },
      {
        path: urlFor.comicbookImpressions(uuid, query),
        message: 'book.impressions',
        count: impressions_count,
        bypassAuth: true,
      },
    ]
  },

  userNav(user: UserLong, query?: QueryParams): NavItem[] {
    const {
      login,
      counters: {
        total,
        impressions,
        quotes,
        bookshelves,
        following_bookshelves,
        following_series,
        updated_series,
      },
      followings_count,
    } = user

    const nav = [
      {
        path: urlFor.userBooksAll(login, query),
        message: 'shelf.books',
        count: total,
      },
      {
        path: urlFor.userShelves(login, query),
        message: 'book.shelves',
        count: bookshelves + following_bookshelves,
      },
      {
        path: urlFor.userSeries(login, query),
        message: 'profile.series_counter',
        count: following_series,
        hasUpdatedSeries: updated_series > 0,
      },
      {
        path: urlFor.userImpressions(login, query),
        message: 'book.impressions',
        count: impressions,
      },
      {
        path: urlFor.userQuotes(login, query),
        message: 'book.quotes',
        count: quotes,
      },
      {
        path: urlFor.userFollowing(login, query),
        message: 'profile.connections',
        count: followings_count,
        pseudoLink: true,
      },
    ]

    return nav
  },

  seriesNav(series: SeriesProps, query?: QueryParams): NavItem[] {
    const {
      uuid,
      followers_count: followersCount,
      parts_count: partsCount,
      kinds,
    } = series

    const isEpisodes = ['podcast', 'comics'].includes(kinds[0])

    const nav = [
      {
        path: urlFor.seriesFollowers(uuid, query),
        message: 'series.followers',
        count: followersCount,
        pseudoLink: true,
      },
      {
        path: urlFor.seriesEpisodes(uuid, query),
        message: isEpisodes ? 'series.episodes' : 'shelf.books',
        count: partsCount,
      },
    ]

    return nav
  },
}
